"use client";

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import Image from "next/image";
import { cn } from "@/lib/utils";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { formatBackgroundStyle } from "@/utils/styles/format";

const IconSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  image: z.string().optional().default(""),
  alt: z.string().optional().default(""),
});

const RowSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  title: z.string().optional(),
  order_index: z.union([z.number(), z.string()]).optional(),
  scroll_frequency: z.union([z.string(), z.number()]).optional().default(3000),
  scroll_direction: z.enum(["left", "right"]).optional().default("left"),
  icons: z.array(IconSchema).optional().default([]),
});

const Placements4DataSchema = z.object({
  status: z
    .union([z.boolean(), z.number()])
    .transform((v) => Boolean(v))
    .optional()
    .default(true),
  id: z.union([z.number(), z.string()]).optional(),
  page_id: z.number().optional(),
  component_id: z.number().optional(),
  variant: z.string().optional(),
  desktop_background_color: z.string().optional().default("#000000"),
  mobile_background_color: z.string().optional().default("#000000"),
  backvariant: z.string().optional().default("variant_3"),
  gradient_color_1: z.string().optional().default(""),
  gradient_color_2: z.string().optional().default(""),
  gradient_color_3: z.string().optional().default(""),
  gradient_color_4: z.string().optional().default(""),
  rows_config: z.array(RowSchema).optional().default([]),
});

type Placements4Data = z.infer<typeof Placements4DataSchema>;
type Placements4Row = z.infer<typeof RowSchema>;

const toFrequencyMs = (value: string | number | undefined): number => {
  const raw = typeof value === "string" ? parseInt(value, 10) : value;
  return Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : 3000;
};

/** Slow-down factor applied to the marquee speed while a row is hovered. */
const HOVER_SLOW_RATE = 0.2;
/** How many pixels of drag equal one pixel of marquee travel (higher = faster drag). */
const DRAG_SPEED_MULTIPLIER = 5;

const ScrollingIconRow: FC<{
  icons: { image?: string; alt?: string }[];
  frequency: number;
  direction: "left" | "right";
  fadeColor: string;
  rowClassName?: string;
}> = ({ icons, frequency, direction, fadeColor, rowClassName }) => {
  const safeMs = toFrequencyMs(frequency); // falls back to 3000ms
  const baseDurationSec = Math.max(0.5, safeMs / 1000);
  const isRight = direction === "right";

  const containerRef = useRef<HTMLDivElement>(null);
  const halfRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const animId = useRef(`p4m${Math.random().toString(36).slice(2, 8)}`).current;
  // How many times the icon list is repeated within one half of the track so a
  // single half already fills the viewport (lets the duplicate scroll in seamlessly).
  const [repeat, setRepeat] = useState(1);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartTimeMs = useRef(0);
  const isHovered = useRef(false);
  // Keep a ref-synced snapshot so drag handlers close over stable refs.
  const rowStateRef = useRef({ baseDurationSec, repeat, isRight });
  rowStateRef.current = { baseDurationSec, repeat, isRight };

  const recalc = useCallback(() => {
    const container = containerRef.current;
    const half = halfRef.current;
    if (!container || !half) return;
    const vw = container.clientWidth;
    const oneListWidth = half.offsetWidth / repeat;
    if (oneListWidth <= 0) return;
    const needed = Math.max(1, Math.ceil(vw / oneListWidth) + 1);
    if (needed !== repeat) setRepeat(needed);
  }, [repeat]);

  useEffect(() => {
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [recalc, icons.length]);

  useEffect(() => {
    const getAnim = () => trackRef.current?.getAnimations()[0] ?? null;

    const onMove = (e: MouseEvent) => {
      if (!isDragging.current || !halfRef.current) return;
      const anim = getAnim();
      if (!anim) return;
      const { baseDurationSec: dur, repeat: rep, isRight: right } = rowStateRef.current;
      const halfWidth = halfRef.current.offsetWidth;
      const totalMs = dur * rep * 1000;
      if (!halfWidth) return;
      const delta = e.clientX - dragStartX.current;
      // For left-scroll: drag left (delta<0) advances time; for right-scroll: drag right (delta>0) advances.
      const raw = dragStartTimeMs.current + (right ? 1 : -1) * delta * (totalMs / halfWidth) * DRAG_SPEED_MULTIPLIER;
      anim.currentTime = ((raw % totalMs) + totalMs) % totalMs;
    };

    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      const anim = getAnim();
      if (!anim) return;
      anim.playbackRate = isHovered.current ? HOVER_SLOW_RATE : 1;
      anim.play();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Slow the marquee down on hover (instead of stopping) via the Web Animations
  // API — changing playbackRate continues smoothly from the current position.
  const setPlaybackRate = (rate: number) => {
    trackRef.current?.getAnimations().forEach((a) => {
      a.playbackRate = rate;
    });
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const anim = trackRef.current?.getAnimations()[0];
    if (!anim) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartTimeMs.current = (anim.currentTime as number) ?? 0;
    document.body.style.cursor = "grabbing";
    anim.pause();
  }, []);

  // One contiguous run of icons, repeated `repeat` times. Equal right padding on
  // every icon (incl. the last) keeps spacing identical across the loop seam.
  const renderRun = () =>
    Array.from({ length: repeat }).flatMap((_, r) =>
      icons.map((icon, i) => (
        <div
          key={`${r}-${i}`}
          className={cn(
            // Each icon expands and lifts above its neighbours on hover.
            "relative shrink-0 transition-transform duration-300 ease-out",
            "hover:z-30 hover:scale-[1.3] hover:-translate-y-1 hover:rotate-[2deg]",
          )}
        >
          <Image
            src={icon.image || ""}
            alt={icon.alt || "Placement partner"}
            width={400}
            height={100}
            className={cn(
              "w-auto object-contain max-w-none transition-[filter] duration-300",
              "hover:drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)]",
              "xs:h-[20px]",
              "sm:h-[34px]",
              "md:h-[40px]"
            )}
            unoptimized
            onLoad={recalc}
          />
        </div>
      ))
    );

  return (
    <div
      ref={containerRef}
      className={cn("w-full relative select-none cursor-ew-resize", rowClassName)}
      onMouseEnter={() => { isHovered.current = true; if (!isDragging.current) setPlaybackRate(HOVER_SLOW_RATE); }}
      onMouseLeave={() => { isHovered.current = false; if (!isDragging.current) setPlaybackRate(1); }}
      onMouseDown={handleMouseDown}
    >
      {/* Two identical halves; translating by exactly -50% loops seamlessly.
          `w-max` makes the track shrink-wrap its content (= 2 halves) so the
          animation's -50% equals exactly one half's width. Without it the track
          fills the container (≈ viewport) width, so -50% no longer matches a
          half and the loop visibly jumps each cycle. */}
      <div
        ref={trackRef}
        className="flex w-max"
        style={{
          animation: `${animId} ${baseDurationSec * repeat}s linear infinite`,
        }}
      >
        <div
          ref={halfRef}
          className="flex xs:gap-[40px] xs:pr-[40px] sm:gap-[80px] sm:pr-[80px] md:gap-[80px] md:pr-[80px] lg:gap-[80px] lg:pr-[80px] xl:gap-[80px] xl:pr-[80px]"
        >
          {renderRun()}
        </div>
        <div
          className="flex xs:gap-[40px] xs:pr-[40px] sm:gap-[80px] sm:pr-[80px] md:gap-[80px] md:pr-[80px] lg:gap-[80px] lg:pr-[80px] xl:gap-[80px] xl:pr-[80px]"
          aria-hidden="true"
        >
          {renderRun()}
        </div>
      </div>

      {fadeColor && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            backgroundImage: `linear-gradient(90deg, ${fadeColor} 0%, transparent 25.6%, transparent 77.4%, ${fadeColor} 100%)`,
          }}
        />
      )}

      <style>{`
        @keyframes ${animId} {
          from { transform: translateX(${isRight ? "-50%" : "0"}); }
          to { transform: translateX(${isRight ? "0" : "-50%"}); }
        }
      `}</style>
    </div>
  );
};

export const Placements4: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;
  const parsedResult = Placements4DataSchema.safeParse(actualData);
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(1080);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "placements_section");
  //   }
  // }, []);

  if (!parsedResult.success) {
    console.error("Placements4 validation failed", parsedResult.error);
    return null;
  }

  const data: Placements4Data = parsedResult.data;
  if (!data.status || isMobile === null) return null;

  const rows = (data.rows_config || [])
    .map((row: Placements4Row) => ({
      ...row,
      icons: (row.icons || []).filter((icon) => !!icon.image?.trim()),
    }))
    .filter((row) => row.icons.length > 0)
    .sort((a, b) => Number(a.order_index ?? 0) - Number(b.order_index ?? 0));

  if (rows.length === 0) return null;

  const backgroundStyle = formatBackgroundStyle(
    {
      backvariant: data.backvariant as
        | "variant_1"
        | "variant_2"
        | "variant_3"
        | undefined,
      gradient_color_1: data.gradient_color_1,
      gradient_color_2: data.gradient_color_2,
      gradient_color_3: data.gradient_color_3,
      gradient_color_4: data.gradient_color_4,
      desktop_background_color: data.desktop_background_color,
      mobile_background_color: data.mobile_background_color,
    },
    isMobile,
    "#000000"
  );
  const fadeColor =
    (isMobile ? data.mobile_background_color : data.desktop_background_color) ||
    "#000000";

  return (
    <section
      ref={ref}
      className={cn(
        "relative w-full overflow-hidden",
        "xs:py-[10px]",
        "sm:py-[20px]",
        "md:py-[20px]",
        "lg:py-[60px]",
        "xl:py-[60px]"
      )}
      style={backgroundStyle}
    >
      <div
        className={cn(
          "mx-auto flex flex-col",
          "xs:gap-[12px]",
          "sm:gap-[14px]",
          "md:gap-[14px]",
          "lg:gap-[14px]",
          "xl:gap-[14px]"
        )}
      >
        {rows.map((row, index) => (
          <ScrollingIconRow
            key={row.id ?? index}
            icons={row.icons}
            frequency={toFrequencyMs(row.scroll_frequency)}
            direction={row.scroll_direction === "right" ? "right" : "left"}
            fadeColor={fadeColor}
            rowClassName={cn(
              "xs:min-h-[40px]",
              "sm:min-h-[60px]",
              "md:min-h-[60px]",
              "lg:min-h-[70px]",
              "xl:min-h-[70px]"
            )}
          />
        ))}
      </div>
    </section>
  );
};

export default Placements4;
